import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { supabase, handleSupabaseError } from '../lib/supabase';
import { useTranslation } from '../hooks/useTranslation';
import { Loader2, Camera, AlertCircle, Check, Upload, Pencil } from 'lucide-react';

export function ProfilePage() {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<{
    id: string;
    first_name: string;
    last_name: string;
    username: string;
    phone: string | null;
    secondary_phone: string | null;
    date_of_birth: string | null;
    avatar_url: string | null;
    email: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    secondaryPhone: '',
    dateOfBirth: '',
  });
  const [showSecondaryPhone, setShowSecondaryPhone] = useState(false);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      setError(null);
      const data = await handleSupabaseError(
        () => supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single(),
        'fetchProfile'
      );

      setProfile(data);
      setFormData({
        firstName: data.first_name || '',
        lastName: data.last_name || '',
        phone: data.phone || '',
        secondaryPhone: data.secondary_phone || '',
        dateOfBirth: data.date_of_birth || '',
      });
      setShowSecondaryPhone(!!data.secondary_phone);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [user]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile?.id) return;

    if (!file.type.startsWith('image/')) {
      setError('Only image files are allowed');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${profile.id}/avatar.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          upsert: true,
          onUploadProgress: (progress) => {
            const percentage = (progress.loaded / progress.total) * 100;
            setUploadProgress(percentage);
          },
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      await fetchProfile();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('Error uploading avatar:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload avatar');
    } finally {
      setLoading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUpdate = async (field: string) => {
    if (!profile?.id) return;

    setLoading(true);
    setError(null);

    try {
      let updateData: any = {};

      switch (field) {
        case 'name':
          updateData = {
            first_name: formData.firstName,
            last_name: formData.lastName,
          };
          break;
        case 'phone':
          updateData = {
            phone: formData.phone,
            secondary_phone: showSecondaryPhone ? formData.secondaryPhone : null,
          };
          break;
        case 'dateOfBirth':
          updateData = {
            date_of_birth: formData.dateOfBirth || null,
          };
          break;
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', profile.id);

      if (updateError) throw updateError;

      await fetchProfile();
      setEditingField(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-800 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <div className="text-lg text-gray-600 dark:text-gray-300">{t.common.loading}</div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-800 flex items-center justify-center p-4">
        <div className="text-center text-gray-600 dark:text-gray-300">
          {error || 'Profile not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800 py-6 px-4 sm:px-6">
      <div className="max-w-lg mx-auto">
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-600">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t.profile.title}
            </h1>
          </div>

          <div className="px-4 py-5 sm:p-6 space-y-6">
            {error && (
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="p-3 bg-green-100 border border-green-400 text-green-700 rounded flex items-start">
                <Check className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                <span>{t.profile.updateSuccess}</span>
              </div>
            )}

            {/* Avatar */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
                  {profile.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={t.profile.avatar}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <Camera className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <label
                  htmlFor="avatar-upload"
                  className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                    ref={fileInputRef}
                  />
                </label>
                {uploadProgress > 0 && (
                  <div className="absolute -bottom-6 left-0 right-0">
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Profile Information */}
            <div className="space-y-6 mt-8">
              {/* Name */}
              <div className="border-b border-gray-200 dark:border-gray-600 pb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t.profile.name}
                  </label>
                  <button
                    onClick={() => setEditingField(editingField === 'name' ? null : 'name')}
                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
                {editingField === 'name' ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder={t.auth.register.firstName}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    />
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder={t.auth.register.lastName}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    />
                    <div className="flex justify-end space-x-2 mt-2">
                      <button
                        onClick={() => setEditingField(null)}
                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400"
                      >
                        {t.common.cancel}
                      </button>
                      <button
                        onClick={() => handleUpdate('name')}
                        className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      >
                        {t.common.save}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-600 dark:text-gray-400">
                    {profile.first_name} {profile.last_name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="border-b border-gray-200 dark:border-gray-600 pb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.auth.register.email}
                </label>
                <p className="text-gray-600 dark:text-gray-400">
                  {profile.email}
                </p>
              </div>

              {/* Username */}
              <div className="border-b border-gray-200 dark:border-gray-600 pb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.auth.register.username}
                </label>
                <p className="text-gray-600 dark:text-gray-400">
                  @{profile.username}
                </p>
              </div>

              {/* Phone Numbers */}
              <div className="border-b border-gray-200 dark:border-gray-600 pb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t.profile.phoneNumbers}
                  </label>
                  <button
                    onClick={() => setEditingField(editingField === 'phone' ? null : 'phone')}
                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
                {editingField === 'phone' ? (
                  <div className="space-y-3">
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+971 50 123 4567"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    />
                    {showSecondaryPhone ? (
                      <input
                        type="tel"
                        value={formData.secondaryPhone}
                        onChange={(e) => setFormData({ ...formData, secondaryPhone: e.target.value })}
                        placeholder="+971 50 123 4567"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowSecondaryPhone(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      >
                        + Add secondary phone
                      </button>
                    )}
                    <div className="flex justify-end space-x-2 mt-2">
                      <button
                        onClick={() => setEditingField(null)}
                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400"
                      >
                        {t.common.cancel}
                      </button>
                      <button
                        onClick={() => handleUpdate('phone')}
                        className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      >
                        {t.common.save}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">
                      {profile.phone || t.profile.notSet}
                    </p>
                    {profile.secondary_phone && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t.profile.secondary}: {profile.secondary_phone}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Date of Birth */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t.profile.dateOfBirth}
                  </label>
                  <button
                    onClick={() => setEditingField(editingField === 'dateOfBirth' ? null : 'dateOfBirth')}
                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
                {editingField === 'dateOfBirth' ? (
                  <div className="space-y-3">
                    <input
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    />
                    <div className="flex justify-end space-x-2 mt-2">
                      <button
                        onClick={() => setEditingField(null)}
                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400"
                      >
                        {t.common.cancel}
                      </button>
                      <button
                        onClick={() => handleUpdate('dateOfBirth')}
                        className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      >
                        {t.common.save}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-600 dark:text-gray-400">
                    {profile.date_of_birth ? 
                      new Date(profile.date_of_birth).toLocaleDateString() : 
                      t.profile.notSet}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}